import React from 'react';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EverythingIcon, SpacesIcon } from './LibraryIcons';
import { CHROME_TOP, TAB_BAR_HEIGHT, TAB_BAR_PAD } from './chromeMetrics';

export type LibraryTab = 'everything' | 'spaces';

/**
 * Top bar: account on the left, the wordmark (or the tab's title) in the
 * middle, the add button on the right. After mymind's header, in Nordic
 * paper: the add button is an ink pill, not orange. The bar is 64px with a
 * 36px pill, so the controls have air around them.
 */
export function LibraryHeader({ title, accountSlot, onAdd }: { title: string; accountSlot?: React.ReactNode; onAdd?: () => void }) {
  return (
    <header className="relative z-20 flex h-16 shrink-0 items-center justify-between bg-paper px-4" style={{ marginTop: CHROME_TOP }}>
      <div className="flex h-9 min-w-9 items-center justify-center">{accountSlot}</div>
      <h1 className="pointer-events-none absolute inset-x-20 truncate text-center font-display text-[21px] font-medium tracking-[-0.01em] text-ink-3">{title}</h1>
      <button
        type="button"
        onClick={onAdd}
        aria-label="Add to your mind"
        className="flex h-9 w-12 items-center justify-center rounded-full bg-ink text-paper transition-transform duration-300 ease-pop active:scale-[0.94] active:duration-100 active:ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal focus-visible:ring-offset-2 focus-visible:ring-offset-paper motion-reduce:transition-none motion-reduce:active:scale-100"
      >
        <Plus className="h-[18px] w-[18px]" strokeWidth={2.2} />
      </button>
    </header>
  );
}

const TABS: { key: LibraryTab; label: string; Icon: typeof EverythingIcon }[] = [
  { key: 'everything', label: 'Everything', Icon: EverythingIcon },
  { key: 'spaces', label: 'Spaces', Icon: SpacesIcon },
];

/**
 * Bottom tab bar. The active tab's mark fills and its label turns ink; the
 * resting one is an outline in ink-3. No rule along the top: the search pill
 * floats above it and the page runs underneath both.
 */
export function LibraryTabBar({ tab, onTabChange }: { tab: LibraryTab; onTabChange: (tab: LibraryTab) => void }) {
  return (
    <nav
      // Glass, as the desktop toolbars: the feed shows through, softened. It runs to
      // the bottom edge; the tabs centre in its 56px row, clear of the home indicator (TAB_BAR_PAD)
      className="pointer-events-auto flex items-stretch border-t border-line/60 bg-paper/75 backdrop-blur-[24px] backdrop-saturate-[1.4]"
      style={{ height: TAB_BAR_HEIGHT, paddingBottom: TAB_BAR_PAD }}
      aria-label="Library"
    >
      {TABS.map(({ key, label, Icon }) => {
        const active = tab === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onTabChange(key)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'group flex flex-1 flex-col items-center justify-center gap-[3px] text-[11px] font-medium tracking-[0.01em] outline-none transition-colors duration-150 ease-out focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-signal',
              active ? 'text-ink' : 'text-ink-3',
            )}
          >
            <span className="transition-transform duration-300 ease-pop group-active:scale-[0.9] group-active:duration-100 group-active:ease-out motion-reduce:transition-none motion-reduce:group-active:scale-100">
              <Icon active={active} className="h-[22px] w-[22px]" />
            </span>
            {label}
          </button>
        );
      })}
    </nav>
  );
}
